/**
 * Google Analytics Event Tracking Helpers
 * Provides type-safe wrappers for sending events to Google Analytics (gtag)
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    uetq?: any[];
  }
}

// Low-level helper to trigger a gtag event safely
export function trackEvent(eventName: string, params?: Record<string, any>) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try {
      window.gtag('event', eventName, params);
      console.log(`[Analytics] Tracked event: ${eventName}`, params);
    } catch (err) {
      console.error('[Analytics] Failed to log event', eventName, err);
    }
  } else {
    // Development/fallback logger
    console.log(`[Analytics - Sandbox] Event log simulated: ${eventName}`, params);
  }
}

/**
 * Standard ecommerce and custom interaction trackers
 */

// User opened the customizer
export function trackCustomizerOpen() {
  trackEvent('customizer_open', {
    category: 'engagement'
  });
}

// User uploaded a photo in the customizer
export function trackPhotoUploaded(fileSize?: number, fileType?: string) {
  trackEvent('photo_uploaded', {
    category: 'builder',
    file_size: fileSize,
    file_type: fileType
  });
}

// User updated/added text in customizer
export function trackTextAdded(textLength: number, textColor: string, fontStyle: string) {
  trackEvent('text_added_to_magnet', {
    category: 'builder',
    text_length: textLength,
    text_color: textColor,
    font_style: fontStyle
  });
}

// User added customized magnet to cart
export function trackAddToCart(magnet: {
  id: string;
  width: number;
  height: number;
  quantity: number;
  price: number;
  shape?: string;
}) {
  const sizeLabel = `${magnet.width}x${magnet.height}`;
  
  // Track GA4 Standard add_to_cart
  trackEvent('add_to_cart', {
    currency: 'CAD',
    value: magnet.price * magnet.quantity,
    items: [
      {
        item_id: magnet.id || 'custom_magnet',
        item_name: `Custom Magnet - ${sizeLabel}`,
        item_category: 'Magnets',
        item_variant: magnet.shape || 'rectangle',
        price: magnet.price,
        quantity: magnet.quantity
      }
    ]
  });
}

// User initiated/started the checkout process
export function trackBeginCheckout(cartItems: Array<any>, subtotal: number) {
  const items = cartItems.map((item, index) => ({
    item_id: item.id || `item_${index}`,
    item_name: `Custom Magnet - ${item.width || 3}x${item.height || 2}`,
    item_category: 'Magnets',
    price: item.price || 5.99,
    quantity: item.quantity || 1
  }));

  trackEvent('begin_checkout', {
    currency: 'CAD',
    value: subtotal,
    items: items
  });
}

// User completed payment / purchased custom magnets
export function trackPurchase(purchase: {
  transaction_id: string;
  value: number;
  tax?: number;
  shipping: number;
  items: Array<{
    id: string;
    width: number;
    height: number;
    price: number;
    quantity: number;
  }>;
}) {
  const items = purchase.items.map((item) => ({
    item_id: item.id,
    item_name: `Custom Magnet - ${item.width}x${item.height}`,
    item_category: 'Magnets',
    price: item.price,
    quantity: item.quantity
  }));

  trackEvent('purchase', {
    transaction_id: purchase.transaction_id,
    currency: 'CAD',
    value: purchase.value,
    shipping: purchase.shipping,
    tax: purchase.tax || 0,
    items: items
  });

  // Google Ads Purchase Conversion Event Snippet
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-1000031590/ViaNCPeZ1bIZEOaK7dwD',
        'value': purchase.value || 1.0,
        'currency': 'CAD',
        'transaction_id': purchase.transaction_id || ''
      });
      console.log(`[Google Ads] Conversion event sent successfully for transaction: ${purchase.transaction_id}, value: ${purchase.value}`);
    } catch (err) {
      console.error('[Google Ads] Failed to send conversion event', err);
    }
  }
}

// User applied a discount promo code
export function trackPromoCodeApplied(promoCode: string, discountAmount: number) {
  trackEvent('promo_code_applied', {
    category: 'checkout',
    promo_code: promoCode,
    discount_amount: discountAmount
  });
}

// User completed custom magnet design saved to cart
export function trackMagnetCustomizationComplete(width: number, height: number, filter: string) {
  trackEvent('magnet_customization_complete', {
    category: 'builder',
    size: `${width}x${height}`,
    filter_style: filter
  });
}

/**
 * Microsoft Advertising UET Enhanced Conversions Tracker
 * Pushes customer email to window.uetq as requested
 */
export function trackUetConversion(email: string) {
  if (typeof window !== 'undefined') {
    try {
      window.uetq = window.uetq || [];
      window.uetq.push('set', {
        'pid': {
          'em': email,
          'ph': '' // Replace with empty string because phone number is not collected
        }
      });
      console.log(`[UET] Pushed enhanced conversion email: ${email}`);
    } catch (err) {
      console.error('[UET] Failed to push enhanced conversion', err);
    }
  }
}

